import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  type TooltipProps,
} from 'recharts';
import { useAppSelector } from 'store/hook';
import { type PlanDataType } from 'views/FleetPlanner/CreateDispatchPlan/Steps/types/steps.types';
import { mergeDispatchPlans } from '../utils/dataFormatters';
import { type GeoTrip } from 'types/geo.types';
import { transformMasterAlgoToPlanDataByBumperNo } from 'views/FleetPlanner/utils/dataFormatters';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Paper,
  Divider,
} from '@mui/material';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import useAppTheme from 'hooks/useAppTheme';

dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * Always returns a UTC hour bucket key.
 */
const getUtcHourBucketKey = (timestamp: string): string => {
  return dayjs.utc(timestamp).startOf('hour').toISOString();
};

/**
 * Builds hourly material buckets based on dumping END time (UTC).
 * picks material from most recent loading
 * for the same device.
 */
const buildHourlyMaterialBuckets = (
  planData: PlanDataType[]
): Record<string, number> => {
  const buckets: Record<string, number> = {};
  const lastLoadByDevice: Record<string, { materialDumped: number } | null> =
    {};
  const sortedPlanData = [...planData].sort(
    (a, b) => dayjs.utc(a.start_ts).valueOf() - dayjs.utc(b.start_ts).valueOf()
  );
  for (const entry of sortedPlanData) {
    if (!entry?.device) continue;
    if (entry.action === 'loading') {
      lastLoadByDevice[entry.device] = {
        materialDumped: Number(entry.material_dumped) || 0,
      };
      continue;
    }
    if (entry.action !== 'dumping') continue;
    if (!entry.end_ts) continue;
    const hourKey = getUtcHourBucketKey(entry.end_ts);
    const material = lastLoadByDevice[entry.device]?.materialDumped ?? 0;
    if (material <= 0) continue;
    buckets[hourKey] = (buckets[hourKey] ?? 0) + material;
  }
  return buckets;
};

/**
 * planned vs actual buckets
 */
const buildHourlyComparisonSeries = (
  plannedBuckets: Record<string, number>,
  actualBuckets: Record<string, number>
): Array<{
  hour: string;
  plannedTonnage: number;
  actualTonnage: number;
}> => {
  const allHours = new Set([
    ...Object.keys(plannedBuckets),
    ...Object.keys(actualBuckets),
  ]);
  return Array.from(allHours)
    .sort()
    .map((hour) => ({
      hour,
      plannedTonnage: plannedBuckets[hour] ?? 0,
      actualTonnage: actualBuckets[hour] ?? 0,
    }));
};

const CustomTooltip = ({
  active,
  payload,
  label,
  formatter,
}: TooltipProps<number, string> & { formatter: (val: any) => string }) => {
  const theme = useTheme();

  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  if (active && payload && payload.length) {
    return (
      <Paper
        elevation={4}
        sx={{
          p: 1.5,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          border: `1px solid ${theme.palette.divider}`,
          minWidth: 160,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {formatter(label)}
        </Typography>
        <Divider sx={{ mb: 1, borderColor: theme.palette.divider }} />
        {payload.map((entry, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: entry.color,
                }}
              >
                {entry.name}:
              </Typography>
            </Box>
            <Typography variant="body2" color="text.primary">
              {entry.value?.toLocaleString()} Tons
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  }
  return null;
};

export const HourlyMaterialComparisonChart = ({
  tripsData,
}: {
  tripsData: Record<string, GeoTrip[]>;
  selectedRange: string;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { customerTz } = useCustomerInfo();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  const approvedPlanData = useAppSelector(
    (state) => state.fleetPlanningReducer.dispatchPlanSummary
  );

  const masterAlgoDataByBumperNo = useMemo(() => {
    return transformMasterAlgoToPlanDataByBumperNo(tripsData);
  }, [tripsData]);

  const formattedMasterAlgoData = useMemo(() => {
    return Object.values(masterAlgoDataByBumperNo.planData)
      .flat()
      .sort(
        (a, b) =>
          dayjs.utc(a.start_ts).valueOf() - dayjs.utc(b.start_ts).valueOf()
      );
  }, [masterAlgoDataByBumperNo.planData]);

  const mergedPlans = useMemo(
    () => mergeDispatchPlans(approvedPlanData.plans),
    [approvedPlanData.plans]
  );

  const chartData = useMemo(() => {
    const plannedBuckets = buildHourlyMaterialBuckets(mergedPlans.planData);
    const actualBuckets = buildHourlyMaterialBuckets(formattedMasterAlgoData);
    const rawChartData = buildHourlyComparisonSeries(
      plannedBuckets,
      actualBuckets
    );

    let runningPlanned = 0;
    let runningActual = 0;

    const cumulativeData = rawChartData.map((item) => {
      runningPlanned += item.plannedTonnage;
      runningActual += item.actualTonnage;

      return {
        ...item,
        plannedTonnage: runningPlanned,
        actualTonnage: runningActual,
      };
    });

    const nowInTz = dayjs().tz(customerTz);
    const currentHourStart = nowInTz.startOf('hour');

    return cumulativeData.map((item) => {
      const itemTime = dayjs(item.hour).tz(customerTz);
      const isFuture = itemTime.isAfter(currentHourStart);
      const isPast = itemTime.isBefore(currentHourStart);

      return {
        ...item,
        actualTonnage: isFuture ? null : item.actualTonnage,
        plannedSolid: isFuture ? null : item.plannedTonnage,
        plannedDotted: isPast ? null : item.plannedTonnage,
      };
    });
  }, [mergedPlans.planData, formattedMasterAlgoData, customerTz]);

  const formatTimestamp = (timestamp: number | string): string => {
    return convertUtcToDisplayTime(
      timestamp,
      preferredTimeDisplayMode,
      timezone,
      'HH:mm'
    );
  };

  return (
    <Card
      elevation={3}
      sx={{
        width: '100%',
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight="bold" color="text.primary">
            {t('hourly_material_movement')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('planned_vs_actual_tonnage')}
          </Typography>
        </Box>

        <Box sx={{ width: '100%', height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.palette.divider}
                vertical={false}
                opacity={0.5}
              />
              <XAxis
                dataKey="hour"
                tickFormatter={formatTimestamp}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                axisLine={{ stroke: theme.palette.divider }}
                tickLine={false}
                dy={10}
              />
              <YAxis
                label={{
                  value: 'Tons',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    fill: theme.palette.text.secondary,
                    textAnchor: 'middle',
                    fontSize: 14,
                  },
                }}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<CustomTooltip formatter={formatTimestamp} />}
                cursor={{
                  stroke: theme.palette.divider,
                  strokeWidth: 1,
                  strokeDasharray: '4 4',
                }}
              />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                payload={[
                  {
                    value: 'Planned',
                    type: 'line',
                    id: 'planned',
                    color: theme.palette.success.main,
                  },
                  {
                    value: 'Actual',
                    type: 'line',
                    id: 'actual',
                    color: theme.palette.primary.main,
                  },
                ]}
                formatter={(value) => (
                  <span style={{ color: theme.palette.text.primary }}>
                    {value}
                  </span>
                )}
              />

              <Line
                connectNulls={false}
                type="monotone"
                dataKey="actualTonnage"
                name="Actual"
                strokeWidth={3}
                stroke={theme.palette.primary.main}
                activeDot={{
                  r: 6,
                  strokeWidth: 0,
                  fill: theme.palette.primary.main,
                }}
                dot={{ r: 3, fill: theme.palette.primary.main, strokeWidth: 0 }}
              />

              <Line
                connectNulls={false}
                type="monotone"
                dataKey="plannedSolid"
                name="Planned"
                strokeWidth={3}
                stroke={theme.palette.success.main}
                activeDot={{
                  r: 6,
                  strokeWidth: 0,
                  fill: theme.palette.success.main,
                }}
                dot={{ r: 3, fill: theme.palette.success.main, strokeWidth: 0 }}
                isAnimationActive={false}
              />

              <Line
                connectNulls={false}
                type="monotone"
                dataKey="plannedDotted"
                name="Planned (Future)"
                strokeWidth={3}
                stroke={theme.palette.success.main}
                strokeDasharray="5 5"
                activeDot={{
                  r: 6,
                  strokeWidth: 0,
                  fill: theme.palette.success.main,
                }}
                dot={{ r: 3, fill: theme.palette.success.main, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};
